/** Result of splitting a valuation identifier into its base id and an optional snapshot number. */
export interface ParsedValuationId {
    /** The bare valuation id (RevaId, order number, GUID, …) without any snapshot suffix. */
    id: string;
    /** The requested snapshot number (≥ 1), or null for a plain (live) valuation id. */
    snapshotNumber: number | null;
}

/**
 * Splits a valuation identifier of the form `<RevaId><sep><snapshotNumber>` into its parts, where the
 * separator is either `.` (the canonical, URL-safe form written by {@link buildValuationId}) or `:`
 * (also accepted for convenience / hand-typed ids).
 *
 * Only ids starting with the unambiguous `REVA-` marker are treated as snapshot ids — RevaIds never
 * contain a `.` or `:` (neither is part of the base64 charset), whereas other identifier kinds
 * (Value-Order GUIDs, Europace references like `v1:<hex>`, …) may, so restricting to the `REVA-`
 * prefix avoids any collision. Any non-matching id (incl. plain RevaIds, order numbers, GUIDs) is
 * returned unchanged with `snapshotNumber: null`.
 */
export function parseValuationId(rawId: string): ParsedValuationId {
    const match = /^(REVA-[^:.]+)[:.]([1-9]\d*)$/.exec(rawId);
    return match ? {id: match[1], snapshotNumber: Number(match[2])} : {id: rawId, snapshotNumber: null};
}

/**
 * Inverse of {@link parseValuationId}: builds the URL/input identifier from a bare id and an optional
 * snapshot number. Uses `.` as separator (URL-safe: not percent-encoded and not part of the base64
 * charset). Returns the bare id unchanged when no snapshot number is given.
 */
export function buildValuationId(id: string, snapshotNumber: number | null): string {
    return snapshotNumber == null ? id : `${id}.${snapshotNumber}`;
}
